import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import clsx from 'clsx';
import Input from '../../components/Input';
import Button from '../../components/Button';
import {
    useCurrentProjectId,
    useEmailNewUser,
    useGetRegistrationLink,
    useNewUser,
} from '../../queries/Accounts';
import { useGetUsers, User } from '../../queries/Users';
import {
    RoleDetail,
    useCreateUserRole,
    useDeleteUserRoles,
    useGetRoleDetail,
    useGetRoles,
    useGetUserRoles,
    UserRole,
} from '../../queries/Roles';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import { Creditor, useGetCreditors } from '../../queries/Creditors';
import { BusinessUnit, useGetBusinessUnits } from '../../queries/BusinessUnits';
import { Project, useGetProjects } from '../../queries/Projects';
import { ChevronIcon, EnvelopeIcon, KeyIcon, LinkIcon, TrashCan } from '../../components/Icons';
import TypeAhead from '../../components/TypeAhead';
import { useGetPermissions } from '../../queries/Permissions';
import StatusBadge, { StatusTone } from '../../components/StatusBadge';
import ExpandablePanel, { EXPAND_DURATION_MS } from '../../components/ExpandablePanel';
import ActionMenu, { ActionMenuItem, ContextMenu } from '../../components/ActionMenu';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Users(props: RouteComponentProps) {
    const roles = useGetRoles();
    const roleDetail = useGetRoleDetail();

    const newUser = useNewUser();
    const newEmailUser = useEmailNewUser();
    const registrationLinkUser = useGetRegistrationLink();
    const getUserRoles = useGetUserRoles();
    const getUserRolesRef = useRef(getUserRoles);
    const newUserRole = useCreateUserRole();
    const deleteUserRoles = useDeleteUserRoles();

    const businessUnits = useGetBusinessUnits(undefined, undefined);
    const projects = useGetProjects(undefined, undefined);
    // Loaded on demand, not on mount. The full creditor list is ~1400 ordered
    // rows; most visits to this screen never touch a creditor-scoped role, and
    // fetching it up front was the single slowest thing the page did.
    const creditors = useGetCreditors(undefined, undefined, undefined, undefined, undefined, true);
    const creditorsRef = useRef(creditors);
    const [creditorsRequested, setCreditorsRequested] = useState(false);

    /** Fetch the creditor list the first time something actually needs it. */
    const ensureCreditors = () => {
        if (creditorsRequested) return;
        setCreditorsRequested(true);
        creditorsRef.current.execute();
    };

    const [copied, setCopied] = useState(undefined as string | undefined);
    const [userName, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userErrorMessage, setUserErrorMessage] = useState('');
    const [emailSendResult, setEmailSendResult] = useState(
        undefined as { userId: string; success: boolean; message: string } | undefined
    );
    const [sendingEmailUserId, setSendingEmailUserId] = useState(undefined as string | undefined);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

    /** Bound to the input: updates on every keystroke so typing feels instant. */
    const [searchInput, setSearchInput] = useState('');
    /** Debounced value the query actually runs on. */
    const [appliedSearch, setAppliedSearch] = useState(undefined as string | undefined);
    const [currentUserName, setCurrentUserName] = useState(undefined as string | undefined);
    const [newRoleName, setNewRoleName] = useState(undefined as string | undefined);
    const [newRoleScope, setNewRoleScope] = useState(undefined as string | undefined);
    const [newRoleCreditorId, setNewRoleCreditorId] = useState(undefined as string | undefined);
    const [newRoleBusinessUnitId, setNewRoleBusinessUnitId] = useState(
        undefined as string | undefined
    );
    const [newRoleProjectId, setNewRoleProjectId] = useState(undefined as string | undefined);
    const [newRoleErrorMessage, setNewRoleErrorMessage] = useState('');

    /**
     * Bumped whenever the scope fields are reset, and used as the TypeAhead
     * key so they remount empty.
     *
     * TypeAhead holds the typed text internally and only re-syncs when its
     * `value` prop changes, so clearing the id here left the box still showing
     * the old project - it looked selected but the server was told nothing,
     * and the form answered "requires a Project Id". Remounting is used rather
     * than making it controlled because `onChanged` clears the id on every
     * keystroke, so a controlled value would be empty mid-typing and wipe what
     * is being typed.
     */
    const [roleFieldsKey, setRoleFieldsKey] = useState(0);

    // Mirrors the Audit screen: `mounted` keeps the detail row rendered while
    // it animates shut, `open` drives the transition.
    const [mountedUserId, setMountedUserId] = useState(undefined as string | undefined);
    const [openUserId, setOpenUserId] = useState(undefined as string | undefined);

    /** The registration link dialog. */
    const [linkDialog, setLinkDialog] = useState(
        undefined as { user: User; link: string; loading: boolean; error: string } | undefined
    );
    const linkInputRef = useRef<HTMLInputElement>(null);

    /** Right-click menu anchored to the pointer. */
    const [contextMenu, setContextMenu] = useState(
        undefined as { x: number; y: number; user: User } | undefined
    );

    // Fourth argument is the broad search: username, first name, last name or email.
    const users = useGetUsers(undefined, undefined, undefined, appliedSearch);

    const usersRef = useRef(users);

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        getUserRolesRef.current.reset();
    }, []);

    // The input value and the value the query runs on are deliberately
    // separate. Previously one state held both: the debounced callback called
    // setUsernameSearch, which re-ran the effect that scheduled the debounce,
    // so typing fed back on itself and clearing took a second to apply.
    useEffect(() => {
        const trimmed = searchInput.trim();
        const next = trimmed === '' ? undefined : trimmed;

        if (next === appliedSearch) return undefined;

        // Clearing applies immediately; typing waits for a pause.
        const timer = window.setTimeout(() => setAppliedSearch(next), next === undefined ? 0 : 300);
        return () => window.clearTimeout(timer);
    }, [searchInput, appliedSearch]);

    // Refetch when the applied term changes. Held in a ref so the effect does
    // not depend on the query object it is resetting.
    useEffect(() => {
        usersRef.current.reset();
    }, [appliedSearch]);

    const postUserDetails = async () => {
        setUserErrorMessage('');

        if (!userName.trim() || !email.trim()) {
            setUserErrorMessage('Username and email are both required.');
            return;
        }

        setIsLoading(true);
        const [error] = await newUser.execute({
            email: email.trim(),
            username: userName.trim(),
            firstName: firstName.trim() || undefined,
            lastName: lastName.trim() || undefined,
        });
        setIsLoading(false);

        if (error) {
            setUserErrorMessage(error);
            return;
        }

        users.reset();
        setIsAddUserModalOpen(false);
        setUsername('');
        setEmail('');
        setFirstName('');
        setLastName('');
    };

    const sendNewEmail = async (existingUser: User) => {
        setUserErrorMessage('');
        setEmailSendResult(undefined);
        setSendingEmailUserId(existingUser.id);
        const result = await newEmailUser.execute({
            callbackUrlRoute: `${process.env.REACT_APP_WEB_URL || ''}verification`,
            userName: existingUser.userName,
        });
        const [error] = result;
        setEmailSendResult({
            userId: existingUser.id,
            success: !error,
            message:
                error ||
                (existingUser.emailConfirmed
                    ? 'Password reset email sent'
                    : 'Registration email sent'),
        });
        users.reset();
        setSendingEmailUserId(undefined);
    };

    /**
     * Fetches the link and shows it in a dialog.
     *
     * The clipboard is deliberately not written here: browsers only allow
     * navigator.clipboard.writeText while a user gesture is still active, and
     * awaiting the request can outlive it - which is why a one-click copy
     * silently produced an empty clipboard. The dialog's Copy button is a
     * fresh click, so it always works, and the link is visible either way.
     */
    const copyRegistrationLink = async (existingUser: User) => {
        setUserErrorMessage('');
        setLinkDialog({ user: existingUser, link: '', loading: true, error: '' });

        const [error, link] = await registrationLinkUser.execute({
            callbackUrlRoute: `${process.env.REACT_APP_WEB_URL || ''}verification`,
            userName: existingUser.userName,
        });

        setLinkDialog({
            user: existingUser,
            link: link || '',
            loading: false,
            error: error || (link ? '' : 'The server did not return a link.'),
        });
    };

    const copyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(value);
        } catch {
            // Clipboard refused (permissions, insecure context). Fall back to
            // selecting the text so the user can copy it manually.
            linkInputRef.current?.select();
        }
    };

    const clearUserFields = () => {
        setUserErrorMessage('');
        setUsername('');
        setEmail('');
        setFirstName('');
        setLastName('');
    };

    useEffect(() => {
        if (newUser.error) {
            setUserErrorMessage(newUser.error || 'Failed to create new user');
        }
    }, [newUser.error]);

    /** Every role assignment for a user, including the same role on different scopes. */
    const getUserRoleAssignments = (user: User) =>
        getUserRoles.data?.filter((i) => i.userName === user.userName) || [];

    const getDistinctUserRolesForUser = (user: User) => {
        const distinctRolesForUser: UserRole[] = [];
        getUserRoleAssignments(user).forEach((i: UserRole) => {
            if (!distinctRolesForUser.some((j: UserRole) => j.roleName === i.roleName)) {
                distinctRolesForUser.push(i);
            }
        });
        return distinctRolesForUser;
    };

    const getStatus = (user: User) => {
        if (!getUserRoles.data) return 'Unable to load roles';
        if (getUserRoleAssignments(user).length === 0) return 'Needs at least 1 role';
        if (!user.emailConfirmed) return 'Registration incomplete';
        return 'Ready to login';
    };

    const getStatusTone = (user: User): StatusTone => {
        if (!getUserRoles.data) return 'neutral';
        if (getUserRoleAssignments(user).length === 0) return 'danger';
        if (!user.emailConfirmed) return 'warning';
        return 'success';
    };

    /** Resolves a role assignment's scope to something readable. */
    const describeScope = (assignment: UserRole) => {
        if (assignment.projectId) {
            const project = projects.data?.items.find((i) => i.id === assignment.projectId);
            return { type: 'Project', name: project?.name || '(unknown project)' };
        }
        if (assignment.businessUnitId) {
            const unit = businessUnits.data?.items.find((i) => i.id === assignment.businessUnitId);
            return { type: 'Business Unit', name: unit?.name || '(unknown business unit)' };
        }
        if (assignment.creditorId) {
            const creditor = creditors.data?.items.find((i) => i.id === assignment.creditorId);
            return {
                type: 'Creditor',
                name: creditor?.name || (creditorsRequested ? '(unknown creditor)' : 'Loading…'),
            };
        }
        return { type: 'Global', name: 'All' };
    };

    /** Roles A-Z, as asked: the API orders by DisplayOrder, which is not guessable. */
    const sortedRoles = useMemo(
        () =>
            [...(roleDetail.data?.items || [])].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            ),
        [roleDetail.data]
    );

    const roleDetailByName = useMemo(() => {
        const map: Record<string, RoleDetail> = {};
        roleDetail.data?.items.forEach((i) => {
            map[i.name] = i;
        });
        return map;
    }, [roleDetail.data]);

    const summary = useMemo(() => {
        const list = users.data || [];
        return {
            total: list.length,
            noRoles: list.filter((u) => getUserRoleAssignments(u).length === 0).length,
            unconfirmed: list.filter((u) => !u.emailConfirmed).length,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.data, getUserRoles.data]);

    const formatRoleCreditors = () => {
        if (creditors && creditors.data && creditors.data.items.length > 0) {
            return creditors.data.items.map((creditor: Creditor) => ({
                id: creditor.id || '',
                name: creditor.name || '',
            }));
        }
        return [];
    };

    const formatRoleBusinessUnits = () => {
        if (businessUnits && businessUnits.data && businessUnits.data.items.length > 0) {
            return businessUnits.data.items.map((businessUnit: BusinessUnit) => ({
                id: businessUnit.id || '',
                name: businessUnit.name || '',
            }));
        }
        return [];
    };

    const formatRoleProjects = () => {
        if (projects && projects.data && projects.data.items.length > 0) {
            return projects.data.items.map((project: Project) => ({
                id: project.id || '',
                name: `${project.jobCode} - ${project.name}` || '',
            }));
        }
        return [];
    };

    const clearNewRoleFields = () => {
        setNewRoleCreditorId(undefined);
        setNewRoleProjectId(undefined);
        setNewRoleBusinessUnitId(undefined);
        setNewRoleErrorMessage('');
        setRoleFieldsKey((key) => key + 1);
    };

    const deleteUserRole = async (userRole: UserRole) => {
        await deleteUserRoles.execute(userRole);
        getUserRoles.reset();
    };

    const postCreateNewUserRole = async (roleName: string) => {
        setNewRoleErrorMessage('');
        if (!currentUserName) {
            setNewRoleErrorMessage('Please select a User Name');
            return;
        }
        const [error] = await newUserRole.execute({
            userName: currentUserName,
            projectId: newRoleProjectId,
            businessUnitId: newRoleBusinessUnitId,
            creditorId: newRoleCreditorId,
            roleName,
        });
        getUserRoles.reset();
        users.reset();

        // Clear the scope on success so the next role starts from an empty
        // field. The role itself stays selected, so the same role can be added
        // to another project without picking it again. On failure everything
        // is left as it was, so the attempt can be corrected and retried.
        if (!error) clearNewRoleFields();
    };

    useEffect(() => {
        setNewRoleErrorMessage(newUserRole.error || '');
    }, [newUserRole.error]);

    const toggleExpanded = (userId: string) => {
        if (openUserId === userId) {
            setOpenUserId(undefined);
            window.setTimeout(() => {
                setMountedUserId((current) => (current === userId ? undefined : current));
            }, EXPAND_DURATION_MS);
            return;
        }
        // The expanded row names the scope of each role, so it needs creditors
        // only when one of them is creditor-scoped.
        if ((getUserRoles.data || []).some((i) => i.creditorId)) ensureCreditors();

        setMountedUserId(userId);
        window.requestAnimationFrame(() => setOpenUserId(userId));
    };

    const selectedRoleDetail = newRoleName ? roleDetailByName[newRoleName] : undefined;

    /**
     * One definition of a row's actions, used by both the "…" button and the
     * right-click menu, so the two can never offer different things.
     */
    const buildUserActions = (user: User): ActionMenuItem[] => {
        const hasRoles = getUserRoleAssignments(user).length > 0;
        const needsRole = 'Give this user a role first';

        // The same link does both jobs: it is a registration link for someone who
        // has never signed in, and the only way to set a new password for someone
        // who has - BIC has no forgot-password or change-password flow. Labelled
        // for whichever case the row is actually in, because "send registration
        // email" on a working account reads like it would reset the account.
        const registered = user.emailConfirmed;

        return [
            {
                label: 'Manage roles',
                icon: <KeyIcon className="w-4 h-4" />,
                onClick: () => {
                    setCurrentUserName(user.userName);
                    // Only this modal and creditor-scoped rows need the list.
                    if (getUserRoleAssignments(user).some((i) => i.creditorId)) ensureCreditors();
                },
            },
            {
                label: (() => {
                    if (sendingEmailUserId === user.id) return 'Sending email…';
                    return registered ? 'Send password reset email' : 'Send registration email';
                })(),
                icon: <EnvelopeIcon className="w-4 h-4" />,
                disabled: !hasRoles || sendingEmailUserId === user.id,
                disabledReason: hasRoles ? undefined : needsRole,
                separatorBefore: registered,
                onClick: () => sendNewEmail(user),
            },
            {
                label: registered ? 'Copy password reset link' : 'Copy registration link',
                icon: <LinkIcon className="w-4 h-4" />,
                disabled: !hasRoles,
                disabledReason: hasRoles ? undefined : needsRole,
                onClick: () => copyRegistrationLink(user),
            },
        ];
    };

    /** The roles a user holds, and what each one lets them do. */
    const renderExpanded = (user: User) => {
        if (mountedUserId !== user.id) return null;

        const assignments = getUserRoleAssignments(user);

        return (
            <ExpandablePanel open={openUserId === user.id}>
                <div className="bg-background border-l-4 border-orange-500 px-5 py-4">
                    {assignments.length === 0 ? (
                        <div className="text-sm text-foreground-muted">
                            This user has no roles, so they cannot access anything yet. Use Manage
                            roles to grant one.
                        </div>
                    ) : (
                        assignments.map((assignment) => {
                            const scope = describeScope(assignment);
                            const detail = roleDetailByName[assignment.roleName];

                            return (
                                <div
                                    key={`${assignment.roleName}-${
                                        assignment.projectId ||
                                        assignment.businessUnitId ||
                                        assignment.creditorId ||
                                        'global'
                                    }`}
                                    className="mb-4 last:mb-0"
                                >
                                    <div className="flex items-center flex-wrap gap-2 mb-1.5">
                                        <span className="text-xs font-bold uppercase tracking-wider text-orange-500">
                                            {assignment.roleName}
                                        </span>
                                        <span className="text-xs text-foreground-muted">
                                            applies to {scope.type}: {scope.name}
                                        </span>
                                        <span className="text-xs text-foreground-muted">
                                            · {detail?.permissionCount ?? 0} permissions
                                        </span>
                                    </div>

                                    <div className="overflow-hidden border border-border rounded-lg shadow-sm">
                                        <table className="w-full border-collapse text-sm">
                                            <thead>
                                                <tr className="bg-surface-muted border-b border-border">
                                                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2 w-1/5">
                                                        Area
                                                    </th>
                                                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2 w-1/3">
                                                        Permission
                                                    </th>
                                                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-foreground-secondary px-3 py-2">
                                                        What it allows
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(detail?.permissions || []).map(
                                                    (permission, index) => (
                                                        <tr
                                                            key={permission.code}
                                                            className={clsx(
                                                                'border-t border-border-subtle',
                                                                index % 2 === 1
                                                                    ? 'bg-surface-muted'
                                                                    : 'bg-surface'
                                                            )}
                                                        >
                                                            <td className="px-3 py-1.5 align-top text-foreground-muted">
                                                                {permission.category}
                                                            </td>
                                                            <td className="px-3 py-1.5 align-top font-medium text-foreground">
                                                                {permission.code}
                                                            </td>
                                                            <td className="px-3 py-1.5 align-top text-foreground-secondary">
                                                                {permission.description}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </ExpandablePanel>
        );
    };

    return (
        <div className="p-4 flex flex-col gap-4">
            {/* ---------------------------------------------------------- */}
            {/* Add user                                                    */}
            {/* ---------------------------------------------------------- */}
            <Modal
                title="Add new user"
                subtitle="Creates the account. They will need at least one role before they can sign in."
                isOpen={isAddUserModalOpen}
                size="md"
                onClose={() => {
                    setIsAddUserModalOpen(false);
                    clearUserFields();
                }}
            >
                <div className="space-y-4">
                    <Input
                        label="Username"
                        placeholder="e.g. jsmith"
                        value={userName}
                        type="text"
                        onChange={(event) => setUsername(event.target.value)}
                    />
                    <Input
                        label="Email"
                        placeholder="name@raubex.com"
                        value={email}
                        type="email"
                        onChange={(event) => setEmail(event.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            label="First name"
                            value={firstName}
                            type="text"
                            onChange={(event) => setFirstName(event.target.value)}
                        />
                        <Input
                            label="Last name"
                            value={lastName}
                            type="text"
                            onChange={(event) => setLastName(event.target.value)}
                        />
                    </div>

                    {userErrorMessage && (
                        <div className="text-danger text-sm">{userErrorMessage}</div>
                    )}

                    <div className="flex flex-row-reverse gap-2 pt-2">
                        <Button onClick={postUserDetails} loading={isLoading}>
                            {isLoading ? 'Creating...' : 'Create user'}
                        </Button>
                        <Button secondary onClick={clearUserFields}>
                            Clear
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* ---------------------------------------------------------- */}
            {/* Manage roles                                                */}
            {/* ---------------------------------------------------------- */}
            <Modal
                title="Manage roles"
                subtitle={currentUserName}
                size="lg"
                onClose={() => {
                    setCurrentUserName(undefined);
                    setNewRoleName(undefined);
                    setNewRoleScope(undefined);
                    clearNewRoleFields();
                }}
                // Derived from the selected user rather than a separate open
                // flag. There used to be both, and moving this action into the
                // "…" menu set the user but not the flag, so the modal never
                // opened. One source of truth cannot drift.
                isOpen={!!currentUserName}
            >
                <div className="mb-8 space-y-3">
                    {/*
                      Every role is listed rather than hidden behind a search
                      box: there are only twelve, and an administrator doing
                      this for the first time has no way to guess the names.
                      Sorted A-Z, with the two things that explain a role -
                      what it must be scoped to, and how much it grants.
                    */}
                    <div className="text-sm font-semibold text-foreground">
                        Add a role
                        <span className="font-normal text-foreground-muted">
                            {' '}
                            — pick one to see what it allows
                        </span>
                    </div>

                    <div className="max-h-56 overflow-y-auto border border-border rounded-lg divide-y divide-border-subtle">
                        {sortedRoles.map((role) => {
                            const isSelected = newRoleName === role.name;
                            const alreadyHeld = (getUserRoles.data || []).some(
                                (i) => i.userName === currentUserName && i.roleName === role.name
                            );

                            return (
                                <button
                                    key={role.name}
                                    type="button"
                                    onClick={() => {
                                        const scope = roles.data?.find((i) => i.name === role.name)
                                            ?.scope;
                                        setNewRoleName(role.name);
                                        setNewRoleScope(scope);
                                        // Only a creditor-scoped role needs the
                                        // creditor picker, so only then fetch it.
                                        if (scope === 'CREDITOR') ensureCreditors();
                                        clearNewRoleFields();
                                    }}
                                    className={clsx(
                                        'w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors',
                                        isSelected
                                            ? 'bg-accent-soft border-l-4 border-orange-500'
                                            : 'border-l-4 border-transparent hover:bg-surface-muted'
                                    )}
                                >
                                    <span className="flex items-baseline gap-2 min-w-0">
                                        <span
                                            className={clsx(
                                                'text-sm font-semibold truncate',
                                                isSelected ? 'text-orange-500' : 'text-foreground'
                                            )}
                                        >
                                            {role.name}
                                        </span>
                                        {alreadyHeld && (
                                            <span className="text-xs text-foreground-muted flex-shrink-0">
                                                already held
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-foreground-muted whitespace-nowrap flex-shrink-0">
                                        {role.scopeType} · {role.permissionCount} permissions
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/*
                      Roles have no description in the database, so the only way
                      to know what one means is what it grants. Show that before
                      it is applied.
                    */}
                    {selectedRoleDetail && (
                        <div className="bg-background border-l-4 border-orange-500 rounded-r-lg px-4 py-3">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-orange-500">
                                    {selectedRoleDetail.name}
                                </span>
                                <span className="text-xs text-foreground-muted">
                                    scoped to {selectedRoleDetail.scopeType} ·{' '}
                                    {selectedRoleDetail.permissionCount} permissions
                                </span>
                            </div>
                            <div className="max-h-40 overflow-y-auto text-sm space-y-0.5">
                                {selectedRoleDetail.permissions.map((permission) => (
                                    <div key={permission.code} className="flex gap-2">
                                        <span className="text-foreground-muted w-20 flex-shrink-0 text-xs pt-0.5">
                                            {permission.category}
                                        </span>
                                        <span className="text-foreground-secondary">
                                            {permission.description}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {newRoleScope && newRoleScope === 'CREDITOR' && (
                        <TypeAhead
                            key={roleFieldsKey}
                            placeholder="Search Creditors"
                            caseSensitive={false}
                            label="Creditor"
                            options={formatRoleCreditors()}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={(event: any) => setNewRoleCreditorId(event.target.id)}
                            onChanged={() => setNewRoleCreditorId(undefined)}
                            onCleared={() => setNewRoleCreditorId(undefined)}
                        />
                    )}

                    {newRoleName && newRoleScope === 'PROJECT' && (
                        <TypeAhead
                            key={roleFieldsKey}
                            placeholder="Search Projects"
                            caseSensitive={false}
                            label="Project"
                            options={formatRoleProjects()}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={(event: any) => setNewRoleProjectId(event.target.id)}
                            onChanged={() => setNewRoleProjectId(undefined)}
                            onCleared={() => setNewRoleProjectId(undefined)}
                        />
                    )}

                    {newRoleName && newRoleScope === 'BUSINESSUNIT' && (
                        <TypeAhead
                            key={roleFieldsKey}
                            placeholder="Search Business Units"
                            caseSensitive={false}
                            label="Business Unit"
                            options={formatRoleBusinessUnits()}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={(event: any) => setNewRoleBusinessUnitId(event.target.id)}
                            onChanged={() => setNewRoleBusinessUnitId(undefined)}
                            onCleared={() => setNewRoleBusinessUnitId(undefined)}
                        />
                    )}

                    {newRoleErrorMessage && (
                        <div className="text-danger text-xs">{newRoleErrorMessage}</div>
                    )}

                    <div className="flex flex-row-reverse">
                        {newRoleName && (
                            <Button onClick={() => postCreateNewUserRole(newRoleName)}>
                                Add role
                            </Button>
                        )}
                    </div>
                </div>

                <div className="text-sm font-semibold text-foreground mb-2">Current roles</div>
                <Table<UserRole>
                    loading={getUserRoles.loading && 'Loading roles'}
                    error={getUserRoles.error && `Could not load roles (${getUserRoles.error})`}
                    data={
                        (getUserRoles.data &&
                            getUserRoles.data.filter((i) => i.userName === currentUserName)) ||
                        []
                    }
                    columns={[
                        { key: 'roleName', title: 'Role' },
                        {
                            key: 'scopeType',
                            title: 'Applies to',
                            // One column instead of three mostly-empty ones -
                            // a role only ever uses a single scope.
                            render: (role) => {
                                const scope = describeScope(role);
                                return (
                                    <span>
                                        <span className="text-foreground-muted">
                                            {scope.type}:{' '}
                                        </span>
                                        {scope.name}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'grants',
                            title: 'Grants',
                            render: (role) =>
                                `${
                                    roleDetailByName[role.roleName]?.permissionCount ?? 0
                                } permissions`,
                        },
                        {
                            key: 'actions',
                            title: '',
                            render: (role) => (
                                <TrashCan
                                    className="cursor-pointer flex items-center"
                                    id=""
                                    onClick={() => deleteUserRole(role)}
                                />
                            ),
                        },
                    ]}
                />
            </Modal>

            {/* ---------------------------------------------------------- */}
            {/* Page                                                        */}
            {/* ---------------------------------------------------------- */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-xl font-semibold">Users</h1>
                    <p className="text-sm opacity-70">
                        Create accounts and control what each person can access.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button secondary link="/users/audit">
                        Audit Report
                    </Button>
                    <Button onClick={() => setIsAddUserModalOpen(true)}>Add user</Button>
                </div>
            </div>

            <div className="flex gap-4 flex-wrap text-sm">
                <span>
                    <strong>{summary.total}</strong> users
                </span>
                <span>
                    <strong>{summary.noRoles}</strong> without roles
                </span>
                <span>
                    <strong>{summary.unconfirmed}</strong> registration incomplete
                </span>
            </div>

            <div className="flex gap-3 flex-wrap items-end">
                <Input
                    value={searchInput}
                    label="Search"
                    placeholder="Name, username or email"
                    type="text"
                    width="w-72"
                    onChange={(event) => setSearchInput(event.target.value)}
                />
                {searchInput !== '' && (
                    <button
                        type="button"
                        className="text-sm underline text-foreground-muted pb-2"
                        onClick={() => setSearchInput('')}
                    >
                        Clear
                    </button>
                )}
            </div>

            <Table<User>
                loading={users.loading && 'Loading users...'}
                error={users.error && `Could not load users (${users.error})`}
                data={users.data}
                renderExpanded={renderExpanded}
                onRowContextMenu={(user, event) => {
                    event.preventDefault();
                    setContextMenu({ x: event.clientX, y: event.clientY, user });
                }}
                getStatus={(user) => (openUserId === user.id ? 'bg-accent-soft border-b-0' : '')}
                columns={[
                    {
                        key: 'userName',
                        title: 'User',
                        render: (user) => (
                            <button
                                type="button"
                                className="flex items-center gap-2 text-left"
                                onClick={() => toggleExpanded(user.id)}
                                title="Show the roles this user holds"
                            >
                                <ChevronIcon
                                    className="w-3 h-3 text-foreground-muted"
                                    open={openUserId === user.id}
                                />
                                <span>
                                    <span className="font-semibold block">
                                        {[user.firstName, user.lastName]
                                            .filter(Boolean)
                                            .join(' ') || user.userName}
                                    </span>
                                    <span className="text-xs opacity-70">{user.userName}</span>
                                </span>
                            </button>
                        ),
                    },
                    { key: 'email', title: 'Email' },
                    {
                        key: 'roles',
                        title: 'Roles',
                        render: (user) => {
                            const assignments = getUserRoleAssignments(user);
                            if (assignments.length === 0) {
                                return <StatusBadge tone="warning" label="No roles" />;
                            }
                            return (
                                <div className="flex flex-wrap gap-1">
                                    {getDistinctUserRolesForUser(user).map((userRole: UserRole) => (
                                        <span
                                            key={userRole.roleName}
                                            title={`${
                                                roleDetailByName[userRole.roleName]
                                                    ?.permissionCount ?? 0
                                            } permissions`}
                                            className="border border-border rounded-md px-2 py-0.5 text-xs bg-surface-muted text-foreground"
                                        >
                                            {userRole.roleName}
                                        </span>
                                    ))}
                                </div>
                            );
                        },
                    },
                    {
                        key: 'status',
                        title: 'Status',
                        render: (user) => (
                            <div className="flex flex-col gap-1">
                                <StatusBadge label={getStatus(user)} tone={getStatusTone(user)} />
                                {emailSendResult?.userId === user.id && (
                                    <span
                                        className={`text-xs ${
                                            emailSendResult.success ? 'text-success' : 'text-danger'
                                        }`}
                                    >
                                        {emailSendResult.success ? '✓ ' : '✗ '}
                                        {emailSendResult.message}
                                    </span>
                                )}
                                {copied === user.id && (
                                    <span className="text-xs text-success">✓ Link copied</span>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'actions',
                        title: 'Actions',
                        render: (user) => (
                            <ActionMenu
                                label={`Actions for ${user.userName}`}
                                items={buildUserActions(user)}
                            />
                        ),
                    },
                ]}
            />

            <Modal
                title={
                    linkDialog?.user.emailConfirmed ? 'Password reset link' : 'Registration link'
                }
                subtitle={
                    linkDialog
                        ? `Send this to ${linkDialog.user.userName} so they can ${
                              linkDialog.user.emailConfirmed
                                  ? 'choose a new password'
                                  : 'set a password'
                          }.`
                        : undefined
                }
                isOpen={!!linkDialog}
                onClose={() => {
                    setLinkDialog(undefined);
                    setCopied(undefined);
                }}
            >
                {linkDialog?.loading && (
                    <div className="text-sm text-foreground-muted">Generating link…</div>
                )}

                {linkDialog && !linkDialog.loading && linkDialog.error && (
                    <div className="text-sm text-danger">{linkDialog.error}</div>
                )}

                {linkDialog && !linkDialog.loading && !linkDialog.error && (
                    <div className="space-y-3">
                        <input
                            ref={linkInputRef}
                            readOnly
                            value={linkDialog.link}
                            onFocus={(event) => event.target.select()}
                            className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground"
                        />
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-foreground-muted">
                                Expires 24 hours from now, or as soon as it is used. Anyone with
                                this link can set the password.
                            </span>
                            <div className="flex items-center gap-2">
                                {copied === linkDialog.link && (
                                    <span className="text-xs text-success">✓ Copied</span>
                                )}
                                <Button onClick={() => copyToClipboard(linkDialog.link)}>
                                    Copy link
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={buildUserActions(contextMenu.user)}
                    onClose={() => setContextMenu(undefined)}
                />
            )}
        </div>
    );
}
